import React, { useState } from "react";
import { Button, Tooltip, InputNumber } from "antd";
import {
  FormOutlined,
  DeleteRowOutlined,
  CheckOutlined,
  FastBackwardOutlined,
  FastForwardOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { findNextQuarterStart, findPrevQuarterStart } from "../../utils/DateUtils";
import { AF_START_ITERATION, Ticket } from "../../services/Ticket";
import Divider from "antd/es/divider";
import moment from "moment";
import SpecSelectionModal from './SpecSelectionModal';


interface ChartMenuToolbarProps {
  // FIXME : Replace the need for Auth with the new way of handling authentication
  selectedTicket: Ticket;
  onMoveAndExtendIteration?: (ticket: Ticket, moveDays: number, extendDays: number) => void;
  RemoveFromTimeline: (ticketId: number) => void;
  isLoaded: boolean;
  onRefreshChildren: () => void;
  onClose: () => void;
  setRefreshOnClose?: () => void;
  children?: Ticket[];
  AddPlanningTask: (selectedTicket: Ticket, spec: string) => void;
  showComments: boolean;
  onToggleComments: () => void;
  showChildren: boolean;
  onToggleChildren: () => void;
  onPeriodChangedStateChange?: (hasChanges: boolean) => void;
  onExecutePeriodSave?: (saveFunc: () => void) => void;
  formHasChanges?: boolean;
  formSaveFunction?: (() => Promise<void>) | null;
}

const ChartMenuToolbar: React.FC<ChartMenuToolbarProps> = ({
  selectedTicket,
  onMoveAndExtendIteration,
  isLoaded,
  onRefreshChildren,
  onClose,
  setRefreshOnClose,
  children,
  RemoveFromTimeline,
  AddPlanningTask,
  showComments,
  onToggleComments,
  showChildren,
  onToggleChildren,
  onPeriodChangedStateChange,
  onExecutePeriodSave,
  formHasChanges = false,
  formSaveFunction = null,
}) => {
  const [move, setMove] = useState<number>(0);
  const [length, setLength] = useState<number>(selectedTicket.length);
  const [showSpecModal, setShowSpecModal] = useState<boolean>(false);

  const hasPeriodChanges = move !== 0 || length !== selectedTicket.length;

  React.useEffect(() => {
    onPeriodChangedStateChange?.(hasPeriodChanges);
  }, [hasPeriodChanges, onPeriodChangedStateChange]);

  const executePeriodSave = React.useCallback(() => {
    if (hasPeriodChanges) {
      onMoveAndExtendIteration?.(selectedTicket, move * 14, (length - selectedTicket.length) * 14);
      setRefreshOnClose?.();
    }
  }, [hasPeriodChanges, move, length, selectedTicket, onMoveAndExtendIteration, setRefreshOnClose]);

  React.useEffect(() => {
    onExecutePeriodSave?.(executePeriodSave);
  }, [executePeriodSave, onExecutePeriodSave]);

  const handleCombinedSave = async () => {
    try {
      if (formHasChanges && formSaveFunction) {
        await formSaveFunction();
      }
      
      if (hasPeriodChanges) {
        executePeriodSave();
      }
      
      onClose();
    } catch (error) {
      console.error('Error during combined save:', error);
    }
  };

  const hasAnyChanges = formHasChanges || hasPeriodChanges;

  return (
    <div style={{ backgroundColor: "#F1F2F3", padding: "10px", borderRadius: "5px" }}>
      <Tooltip title="Save changes">
        <Button
          type="primary"
          size="small"
          onClick={handleCombinedSave}
          {...({ disabled: !hasAnyChanges } as any)}
        >
          <SaveOutlined />
        </Button>
      </Tooltip>

      <Divider type="vertical" />

      <Tooltip title="Add Planning Tasks">
        <Button
          type="primary"
          size="small"
          children={<FormOutlined />}
          onClick={() => setShowSpecModal(true)}
        />
      </Tooltip>

      <Tooltip title="Remove from Timeline">
        <Button
          type="primary"
          size="small"
          children={<DeleteRowOutlined />}
          onClick={() => {
            RemoveFromTimeline(selectedTicket.id);
            onClose();
          }}
        />
      </Tooltip>

      <Tooltip title={showComments ? "Hide Comments" : "Show Comments"}>
        <Button
          type={showComments ? "default" : "primary"}
          size="small"
          children={<FormOutlined />}
          onClick={onToggleComments}
        />
      </Tooltip>

      <Tooltip title={showChildren ? "Hide Children" : "Show Children"}>
        <Button
          type={showChildren ? "primary" : "default"}
          size="small"
          onClick={onToggleChildren}
        >
          Children
        </Button>
      </Tooltip>

{/* Removing this for now until I decide how to build this in the future */}
      {/* {planningProject && (
        <>
          <Divider type="vertical" />
          <Tooltip
            title={
              showCreateTeamDependency
                ? "Hide Create Initiative in Team projects"
                : "Create Initiative in Team projects"
            }
          >
            <Button
              type={showCreateTeamDependency ? "default" : "primary"}
              size="small"
              children={<PlusOutlined />}
              onClick={() => {
                setShowCreateLink(false);
                setShowCreateTeamDependency(!showCreateTeamDependency);
              }}
            />
          </Tooltip>
        </>
      )}

      {planningProject && (
        <Tooltip title={showCreateLink ? "Hide Link " : "Link Predecessor to existing ticket"}>
          <Button
            type={showCreateLink ? "default" : "primary"}
            size="small"
            children={<LinkOutlined />}
            onClick={() => {
              setShowCreateTeamDependency(false);
              setShowCreateLink(!showCreateLink);
            }}
          />
        </Tooltip>
      )}
      <Tooltip title={_show_hide_text}>
        <Button
          type={includeTeamDependencies ? "default" : "primary"}
          size="small"
          children={<ApartmentOutlined />}
          onClick={() => {
            setIncludeTeamDependencies(!includeTeamDependencies);
          }}
        />
      </Tooltip>
      */}

      <Divider type="vertical" />

      <label>Move: </label>
      <InputNumber
        min={-12}
        max={12}
        value={move}
        onChange={(value: any) => setMove(value || 0)}
      />
      &nbsp;&nbsp;

      <label>Length: </label>
      <InputNumber
        min={1}
        max={24}
        value={length}
        onChange={(value: any) => setLength(value || selectedTicket.length)}
      />
      &nbsp;&nbsp;

      <Tooltip title="Update period and length">
        <Button
          type="primary"
          size="small"
          children={<CheckOutlined />}
          onClick={() => {
            if (move !== 0 || length !== selectedTicket.length) {
              onMoveAndExtendIteration?.(selectedTicket, move * 14, (length - selectedTicket.length) * 14);
              setRefreshOnClose?.();
              onClose();
            }

          }}
          // FIXME: Uncomment the line below when the condition is defined
          // disabled={unchangedPeriod}
        />
      </Tooltip>

      <Divider type="vertical" />
      <Tooltip title="Move to Previous Quarter">
        <Button
          type="primary"
          size="small"
          children={<FastBackwardOutlined />}
          onClick={() => {
            const startIteration = moment(selectedTicket.fields[AF_START_ITERATION]);
            const newStartDate = findPrevQuarterStart(startIteration.toDate());
            const moveNumberOfDays = moment(newStartDate).diff(startIteration, "days");

            onMoveAndExtendIteration?.(selectedTicket, moveNumberOfDays, 0);
            setRefreshOnClose?.();
            onClose();
          }}
        />
      </Tooltip>

      <Tooltip title="Move to Next Quarter">
        <Button
          type="primary"
          size="small"
          children={<FastForwardOutlined />}
          onClick={() => {
            const startIteration = moment(selectedTicket.fields[AF_START_ITERATION]);
            const newStartDate = findNextQuarterStart(startIteration.toDate());
            const moveNumberOfDays = moment(newStartDate).diff(startIteration, "days")+1;

            onMoveAndExtendIteration?.(selectedTicket, moveNumberOfDays, 0);
            setRefreshOnClose?.();
            onClose();
          }}
        />
      </Tooltip>

      <br />

        {/* <>
          <label>Link to Ticket ID: </label> &nbsp;
          <InputNumber
            min={0}
            max={999999} // Set an appropriate max value
            value={linkToTicketID}
            onChange={(value: any) => setLinkToTicketID(value || 0)}
          />
          <Tooltip title="Link">
            <Button
              type="primary"
              size="small"
              children={<CheckOutlined />}
              onClick={() => {
                setRefreshOnClose?.();
                if (linkToTicketID !== 0 && linkToTicketID !== selectedTicket.id) {
                  console.warn("createLink function is missing");
                }
              }}
            />
          </Tooltip>
        </> */}
  

        {/* <>
          <hr />
          <table style={{ width: "100%" }}>
            <thead>
              <tr>
                <th style={{ width: "15%" }}>Project</th>
                <th style={{ width: "60%" }}>Title</th>
                <th style={{ width: "10%" }}>Ticket ID</th>
              </tr>
            </thead>
            <tbody>
              {selectedTicket.Teams?.map((team: {} | null | undefined) => {
                const _teamSetting = tConst.TEAMS_LIST.find((item) => item.team === team);

                if (
                  children?.some(
                    (child) => child.project === _teamSetting?.project
                  )
                ) {
                  return children
                    ?.filter((child) => child.project === _teamSetting?.project)
                    .map((child) => (
                      <tr key={child.id}>
                        <td>{child.project}</td>
                        <td>{child.title}</td>
                        <td>{child.id}</td>
                      </tr>
                    ));
                } else {

                  return (
                    <tr key={String(team)}>
                      <td>{team}</td>
                      <td></td>
                      <td>
                        <Button
                          // FIXME: Uncomment the line below when the condition is defined
                          // disabled={_allowAddTicketsInTeamProject}
                          type="primary"
                          size="small"
                          children={<PlusOutlined />}
                          onClick={() => {
                            console.warn("createChildrenTicket function is missing");
                          }}
                        />
                      </td>
                    </tr>
                  );
                }
              })}
            </tbody>
          </table>
        </> */}

      <SpecSelectionModal
        visible={showSpecModal}
        selectedTicket={selectedTicket}
        onConfirm={(selectedSpecs) => {
          selectedSpecs.forEach((spec: string) => {
            AddPlanningTask(selectedTicket, spec);
          });
          setShowSpecModal(false);
          onClose();
        }}
        onCancel={() => setShowSpecModal(false)}
      />
    </div>
  );
};


export default ChartMenuToolbar;
